import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, X, Loader2, ArrowRightLeft, ArrowRight } from 'lucide-react';
import api from '../../utils/api';
import usePermission from '../../hooks/usePermission';
import { fmt4, r4, thicknessOptions, keyLabel } from '../../utils/cft';
import { useSettings } from '../../context/SettingsContext';

// CFT page → Transfers: move CFT from one thickness category of a wood to another
// (e.g. White Ash ALL CFT → 38 mm after the boards are sorted). The wood's total stays the same.
const today = () => { const x = new Date(); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };
const fmtDate = d => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function WoodTransfersTab() {
  const { canWrite, canEdit, canDelete } = usePermission('cft');
  const [rows, setRows] = useState([]);
  const [woods, setWoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ from: '', to: '', wood_type_id: '' });
  const [form, setForm] = useState(null);

  useEffect(() => { api.get('/wood-types').then(r => setWoods(r.data.rows || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.get('/wood-transfers', { params }); setRows(r.data || []);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const del = async t => {
    if (!window.confirm(`Delete ${t.transfer_no}? The ${fmt4(t.cft)} CFT goes back to ${t.from_label}.`)) return;
    try { const r = await api.delete(`/wood-transfers/${t.id}`); toast.success(r.data.message); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };
  const total = r4(rows.reduce((a, t) => a + Number(t.cft || 0), 0));

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-gray-200 p-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
        <label className="text-xs text-gray-500">From<input type="date" value={f.from} onChange={e => setF({ ...f, from: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">To<input type="date" value={f.to} onChange={e => setF({ ...f, to: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">Wood
          <select value={f.wood_type_id} onChange={e => setF({ ...f, wood_type_id: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-800">
            <option value="">All woods</option>{woods.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select></label>
        {canWrite && <button onClick={() => setForm({ transfer_date: today(), wood_type_id: '', from_key: 'ALL', to_key: '', cft: '', remarks: '' })}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold"><Plus className="w-4 h-4" /> New Transfer</button>}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? <div className="p-10 text-center text-sm text-gray-400">Loading…</div>
          : !rows.length ? <div className="p-10 text-center text-sm text-gray-400">No transfers{canWrite ? ' — use New Transfer to move stock from one thickness to another, e.g. ALL CFT → 38 mm after sorting.' : ''}</div>
          : (
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-3 py-2.5">Transfer No.</th><th className="px-3 py-2.5">Date</th><th className="px-3 py-2.5">Wood</th><th className="px-3 py-2.5">From → To</th>
                <th className="px-3 py-2.5 text-right">CFT</th><th className="px-3 py-2.5">Remarks</th><th className="px-3 py-2.5">Entered By</th><th></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-semibold text-purple-700 whitespace-nowrap">{t.transfer_no}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(t.transfer_date)}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">{t.wood_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><span className="font-semibold">{t.from_label}</span> <ArrowRight className="w-3.5 h-3.5 inline text-gray-400" /> <span className="font-semibold">{t.to_label}</span></td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">{fmt4(t.cft)}</td>
                    <td className="px-3 py-2 text-gray-600">{t.remarks || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{t.created_by_name || '—'}</td>
                    <td className="px-3 py-2"><div className="flex justify-end gap-1">
                      {canEdit && <button onClick={() => setForm({ ...t, wood_type_id: String(t.wood_type_id), cft: Number(t.cft) })} title="Edit" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Edit className="w-4 h-4" /></button>}
                      {canDelete && <button onClick={() => del(t)} title="Delete" className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="bg-gray-50 font-bold"><td className="px-3 py-2" colSpan={4}>Total ({rows.length} transfers)</td><td className="px-3 py-2 text-right">{fmt4(total)}</td><td colSpan={3}></td></tr></tfoot>
            </table>
          )}
      </div>
      {form && <TransferForm form={form} woods={woods} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function TransferForm({ form, woods, onClose, onSaved }) {
  const { settings } = useSettings() || {};
  const allowNegative = String(settings?.cft_allow_negative_stock || 'no').toLowerCase() === 'yes';
  const [f, setF] = useState(form);
  const [saving, setSaving] = useState(false);
  const [avail, setAvail] = useState(null);
  const wood = woods.find(w => String(w.id) === String(f.wood_type_id));
  const keep = [form.from_key, form.to_key].filter(k => k && k !== 'ALL');
  const keys = [...thicknessOptions(wood, keep).map(String), 'ALL'];
  useEffect(() => {
    if (!f.wood_type_id || !f.transfer_date) { setAvail(null); return; }
    api.get('/wood-stock/available', { params: { wood_type_id: f.wood_type_id, as_of: f.transfer_date, exclude_transfer: f.id || undefined } })
      .then(r => setAvail(r.data.buckets || {})).catch(() => setAvail(null));
  }, [f.wood_type_id, f.transfer_date, f.id]);
  const fromAvail = avail ? r4(avail[f.from_key] || 0) : null;
  const toAvail = avail && f.to_key ? r4(avail[f.to_key] || 0) : null;
  const cft = r4(Number(f.cft) || 0);
  const tooMuch = !allowNegative && fromAvail !== null && cft > 0 && fromAvail - cft < -0.00005;
  const save = async () => {
    if (!f.wood_type_id) return toast.error('Choose the wood type');
    if (!f.from_key || !f.to_key) return toast.error('Choose From and To');
    if (f.from_key === f.to_key) return toast.error('From and To must be different');
    if (!(cft > 0)) return toast.error('Enter the CFT to transfer');
    setSaving(true);
    try {
      const body = { transfer_date: f.transfer_date, wood_type_id: f.wood_type_id, from_key: f.from_key, to_key: f.to_key, cft, remarks: f.remarks };
      const r = f.id ? await api.put(`/wood-transfers/${f.id}`, body) : await api.post('/wood-transfers', body);
      toast.success(r.data.message);
      if (r.data.warning) toast(r.data.warning, { icon: '⚠️', duration: 6000 });
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const Pick = ({ value, onChange, label }) => (
    <label className="text-xs font-semibold text-gray-500">{label}
      <select className="winp mt-1" value={value} onChange={e => onChange(e.target.value)} disabled={!wood}>
        <option value="">— Choose —</option>{keys.map(k => <option key={k} value={k}>{keyLabel(k)}</option>)}
      </select></label>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-purple-600" /> {f.id ? `Edit ${f.transfer_no}` : 'New Transfer'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-gray-500">Date *<input type="date" className="winp mt-1" value={f.transfer_date} onChange={e => setF({ ...f, transfer_date: e.target.value })} /></label>
            <label className="text-xs font-semibold text-gray-500">Wood Type *
              <select className="winp mt-1" value={f.wood_type_id} onChange={e => setF({ ...f, wood_type_id: e.target.value, to_key: '' })}>
                <option value="">— Select wood —</option>{woods.filter(w => w.is_active || String(w.id) === String(form.wood_type_id)).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select></label>
            <Pick label="From *" value={f.from_key} onChange={v => setF({ ...f, from_key: v })} />
            <Pick label="To *" value={f.to_key} onChange={v => setF({ ...f, to_key: v })} />
          </div>
          {avail && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-2">Available in {keyLabel(f.from_key)}: <b className="tabular-nums">{fmt4(fromAvail)}</b>
                {cft > 0 && <div className={fromAvail - cft < 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}>After: {fmt4(fromAvail - cft)}</div>}</div>
              {f.to_key && <div className="rounded-lg bg-gray-50 border border-gray-200 p-2">In {keyLabel(f.to_key)}: <b className="tabular-nums">{fmt4(toAvail)}</b>
                {cft > 0 && <div className="text-gray-500">After: {fmt4(toAvail + cft)}</div>}</div>}
            </div>
          )}
          <label className="text-xs font-semibold text-gray-500 block">CFT to move *<input type="number" min="0" step="0.0001" className="winp mt-1" value={f.cft} onChange={e => setF({ ...f, cft: e.target.value })} /></label>
          <label className="text-xs font-semibold text-gray-500 block">Remarks<input className="winp mt-1" maxLength={2000} value={f.remarks || ''} onChange={e => setF({ ...f, remarks: e.target.value })} placeholder="e.g. sorted after unloading" /></label>
          {tooMuch && <p className="text-xs font-semibold text-red-600">Only {fmt4(fromAvail)} CFT is in {keyLabel(f.from_key)} on this date — negative stock is off in Settings.</p>}
          <p className="text-[11px] text-gray-400">The wood's total stock doesn't change — the CFT leaves one thickness and enters the other on this date.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving || tooMuch} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
      <style>{`.winp{display:block;width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;font-weight:400;color:#111827;background:#fff} .winp:disabled{background:#f9fafb;color:#4b5563}`}</style>
    </div>
  );
}
